/* eslint-disable no-console */
/**
 * @fileoverview Hook of Actions Layer for optimized management of WebSocket events in the Inbox.
 * Allows handling business actions derived from real-time events (messages and customers) with strict layer separation.
 * Meets the standards of the architecture and documentation of Mercately (v3.1.0).
 * @module useInboxSocketActions
 * @category Hook - Actions Layer
 * @subcategory Socket Management
 * @since 3.1.0
 * @version 3.2.0 - Documentation JSDoc enriched, typedefs and expanded examples.
 */

import { useCallback, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateOrAddMessageOptimized } from "../../../../actions/inbox/messagesOptimizedActions";
import {
  addUnreadChat,
  removeUnreadChat,
  updatePlatformCountFromMsEvent
} from "../../../../actions/inbox/unreadChatsActions";
import {
  refreshMergedPlatformCounts,
  setChatReadToggleState
} from "../../../../actions/inbox/conversationActions";
import {
  setCustomerOrderCount,
  updateCustomerData,
  setLeadScoreIfActive,
  addFieldCaptureIfActive
} from "../../../../actions/inbox/customerDataActions";
import { updateOrInsertConversationOptimized } from "../../../../actions/inbox/conversationOptimizedActions";
import {
  canViewConversationByAssignment,
  conversationMatchesFilter,
  extractAssignedAgentId,
  isUnreadOnlyCustomerUpdate,
  normalizeAgentId,
  resolveAssignedAgentIdForUnreadCounter
} from "../../../../services/inbox/conversationFilterEvaluator";
import {
  buildConversationChangesFromCounterEvent,
  resolveInboxPlatform,
  resolveSidebarCounterPlatform,
  shouldUseMonolithCounterSource
} from '../../../../services/inbox/dualCountersMergeService';
import { sharingInformationService } from "../../../../services/sharingInformationService";
import { isInboxSocketDebugEnabled } from "../../../utils/inboxSocketDebug";

/**
 * Hook of Actions Layer for optimized management of WebSocket events in the Inbox.
 *
 * Implements the **Actions Layer** pattern with strict separation of responsibilities,
 * focusing on the management of business actions derived from real-time events (messages and customers).
 */
function useInboxSocketActions() {
  /**
   * Redux dispatcher to execute actions
   */
  const dispatch = useDispatch();

  // Stabilize dispatch to avoid handler recreation
  const dispatchRef = useRef(dispatch);
  dispatchRef.current = dispatch;

  const activeFilter = useSelector((state) => state.conversationsReducer.activeFilter);

  const currentUserContext = useSelector((state) => state.mainReducer?.currentRetailerUser);
  const currentUserId = currentUserContext?.id;

  const selectedId = useSelector((state) => state.conversationsReducer.selectedConversationId);

  const conversationIndexMap = useSelector(
    (state) => state.conversationsReducer.conversationIndexMap
  );
  const conversationIndexMapRef = useRef(conversationIndexMap);
  conversationIndexMapRef.current = conversationIndexMap;

  const conversationsData = useSelector(
    (state) => state.conversationsReducer.data
  );

  const inboxMsPlatforms = useSelector(
    (state) => state.retailerUsersReducer?.retailer_info?.inbox_microservice_platforms
  );
  const inboxMsPlatformsRef = useRef(inboxMsPlatforms);
  inboxMsPlatformsRef.current = inboxMsPlatforms;

  const msCounterRefetchTimerRef = useRef(null);

  const scheduleMsCounterRefetch = useCallback(() => {
    if (msCounterRefetchTimerRef.current) {
      clearTimeout(msCounterRefetchTimerRef.current);
    }
    msCounterRefetchTimerRef.current = setTimeout(() => {
      dispatchRef.current(refreshMergedPlatformCounts({ force: true }));
      msCounterRefetchTimerRef.current = null;
    }, 2000);
  }, []);

  const hasNonEmptyObject = useCallback((value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length > 0, []);
  const normalizeIdToString = useCallback((value) => {
    if (value === null || value === undefined || value === '') return null;
    return String(value);
  }, []);

  // Drops status_chat from changes when the value is not a recognized enum entry.
  // Guards against legacy API responses that send fabricated values like 'read'/'unread',
  // which would corrupt the conversation filter and cause chats to vanish from the pending list.
  const sanitizeStatusChat = useCallback((attributes) => {
    if (!attributes || !('status_chat' in attributes)) return attributes;
    const { status_chat: statusChatValue, ...rest } = attributes;
    const VALID_STATUS_CHAT = new Set([
      'new_chat', 'open_chat', 'in_process', 'resolved',
      'new_interaction', 'open_interaction', 'in_process_interaction', 'resolved_interaction',
      0, 1, 2, 3
    ]);
    const numericValue = Number(statusChatValue);
    const isValid = VALID_STATUS_CHAT.has(statusChatValue)
      || (!Number.isNaN(numericValue) && VALID_STATUS_CHAT.has(numericValue));
    return isValid ? attributes : rest;
  }, []);

  /**
   * Memoized handler for 'new_message' events.
   */
  const handleNewMessage = useCallback((event) => {
    try {
      if (isInboxSocketDebugEnabled()) {
        console.log('Nuevo mensaje recibido:', event);
      }
      const chatId = event?.chat?.id;
      const normalizedChatId = normalizeIdToString(chatId);
      const normalizedSelectedId = normalizeIdToString(selectedId);
      const chatAttributes = event?.chat?.attributes;
      const messagePayload = event?.message;

      if (!normalizedChatId || !hasNonEmptyObject(chatAttributes) || !messagePayload || typeof messagePayload !== 'object') {
        if (isInboxSocketDebugEnabled()) {
          console.warn('Evento new_message incompleto ignorado:', event);
        }
        return;
      }

      if (normalizedChatId !== normalizedSelectedId) {
        const conversation = {
          id: normalizedChatId,
          type: event?.chat?.type,
          attributes: chatAttributes
        };

        const shouldNotify = conversationMatchesFilter(
          conversation,
          activeFilter,
          currentUserId,
          { currentRetailerUser: currentUserContext }
        );

        if (shouldNotify) {
          window.dispatchEvent(
            new CustomEvent('inbox:new-message', {
              detail: event
            })
          );
        }
      }

      dispatchRef.current(
        updateOrInsertConversationOptimized({
          id: normalizedChatId,
          changes: chatAttributes,
          isUpsert: true,
          conversationListType: event?.chat?.type
        })
      );

      const normalizedMessage = {
        ...messagePayload,
        platform: messagePayload?.channel,
        identifier: 'identifier' in (messagePayload || {})
          ? messagePayload.identifier
          : (messagePayload?.message_identifier || messagePayload?.comment_identifier || messagePayload?.id)
      };

      dispatchRef.current(updateOrAddMessageOptimized(normalizedMessage));

      const platform = resolveInboxPlatform(event?.chat?.type);
      if (!platform) {
        return;
      }

      const sidebarPlatform = resolveSidebarCounterPlatform(platform);
      const trackUnreadViaMonolith = sidebarPlatform
        && shouldUseMonolithCounterSource(sidebarPlatform, inboxMsPlatformsRef.current);
      const isSelectedChat = normalizedSelectedId != null && normalizedChatId === normalizedSelectedId;
      const isInboundMessage = messagePayload.direction === 'inbound';

      if (isSelectedChat && isInboundMessage && platform) {
        sharingInformationService.setSubject({ flushConsolidation: true });
        dispatchRef.current(setChatReadToggleState([{
          id: normalizedChatId,
          platform
        }]));
        if (trackUnreadViaMonolith) {
          dispatchRef.current(removeUnreadChat({ platform, id: normalizedChatId }));
        }
      } else if (isInboundMessage && platform && trackUnreadViaMonolith) {
        const assignedAgentId = extractAssignedAgentId(chatAttributes);
        dispatchRef.current(addUnreadChat({
          platform,
          chat: {
            id: normalizedChatId
          },
          assignedAgentId,
          trustRoomAuthorization: assignedAgentId == null
        }));
      }
    } catch (error) {
      console.error('Error processing new_message:', error);
    }
  }, [
    activeFilter,
    currentUserContext,
    currentUserId,
    selectedId,
    hasNonEmptyObject,
    normalizeIdToString
  ]);

  /**
   * Handler memoizado para eventos 'customer_update'.
   */
  const handleCustomerUpdate = useCallback((event) => {
    try {
      if (isInboxSocketDebugEnabled()) {
        console.log('Actualización de cliente recibida:', event);
      }

      if (!event?.id || !hasNonEmptyObject(event?.attributes)) {
        if (isInboxSocketDebugEnabled()) {
          console.warn('Evento customer_update incompleto ignorado:', event);
        }
        return;
      }

      const normalizedEventId = normalizeIdToString(event?.id);
      if (!normalizedEventId) {
        return;
      }

      const eventAttributes = event?.attributes || {};
      const conversationKey = normalizedEventId;
      const hasAssignedAgentUpdate = Object.prototype.hasOwnProperty.call(
        eventAttributes,
        'assigned_agent'
      );
      const hasMiaChatbotActiveUpdate = Object.prototype.hasOwnProperty.call(
        eventAttributes,
        'mia_chatbot_active'
      );
      const conversationExists = conversationIndexMapRef.current[conversationKey] !== undefined;
      const shouldUpsertConversation = conversationExists
        || hasAssignedAgentUpdate
        || hasMiaChatbotActiveUpdate;

      const isChatMarkedRead = eventAttributes.has_unread_messages === false
        || eventAttributes.unread === false
        || (Object.prototype.hasOwnProperty.call(eventAttributes, 'unread_messages_count')
          && Number(eventAttributes.unread_messages_count) === 0
          && eventAttributes.has_unread_messages !== true);

      const isUnreadOnlyUpdate = isUnreadOnlyCustomerUpdate(eventAttributes);
      const assignedAgentId = resolveAssignedAgentIdForUnreadCounter({
        eventAttributes,
        conversationsData,
        conversationIndexMap,
        chatId: event?.id
      });
      const canViewForUnreadCounter = isUnreadOnlyUpdate
        || canViewConversationByAssignment(currentUserContext, currentUserId, assignedAgentId);

      const newAssignedAgentId = normalizeAgentId(extractAssignedAgentId(eventAttributes));
      const isAssignmentToCurrentUser = hasAssignedAgentUpdate
        && newAssignedAgentId != null
        && newAssignedAgentId === normalizeAgentId(currentUserId);

      if (shouldUpsertConversation) {
        const sanitizedAttributes = sanitizeStatusChat(eventAttributes);
        const conversationChanges = { ...sanitizedAttributes };
        if (event?.type && conversationChanges.platform == null) {
          conversationChanges.platform = event.type;
        }
        if (conversationChanges.unread === false && conversationChanges.has_unread_messages === undefined) {
          conversationChanges.has_unread_messages = false;
        }
        if (isChatMarkedRead && conversationChanges.has_unread_messages === undefined) {
          conversationChanges.has_unread_messages = false;
        }
        if (event?.attributes?.lead_score_data) {
          const scoreData = Array.isArray(event.attributes.lead_score_data)
            ? event.attributes.lead_score_data[1]
            : event.attributes.lead_score_data;
          if (scoreData) {
            conversationChanges.lead_score = scoreData.lead_score;
          }
        }
        dispatchRef.current(updateOrInsertConversationOptimized({
          id: normalizedEventId,
          changes: conversationChanges,
          isUpsert: hasAssignedAgentUpdate || hasMiaChatbotActiveUpdate,
          conversationListType: event?.type
        }));
      }

      dispatchRef.current(updateCustomerData({
        id: normalizedEventId,
        ...eventAttributes
      }));

      if (eventAttributes.orders_count !== undefined) {
        dispatchRef.current(setCustomerOrderCount(eventAttributes.orders_count));
      }

      if (event?.attributes?.lead_score_data) {
        const scoreData = Array.isArray(event.attributes.lead_score_data)
          ? event.attributes.lead_score_data[1]
          : event.attributes.lead_score_data;
        if (scoreData) {
          dispatchRef.current(setLeadScoreIfActive(normalizedEventId, scoreData));
        }
      }

      if (event?.attributes?.field_capture_data) {
        const captureData = Array.isArray(event.attributes.field_capture_data)
          ? event.attributes.field_capture_data[1]
          : event.attributes.field_capture_data;
        if (captureData) {
          dispatchRef.current(addFieldCaptureIfActive(normalizedEventId, captureData));
        }
      }

      const platform = resolveInboxPlatform(event?.type);
      if (!platform) {
        return;
      }

      const sidebarPlatform = resolveSidebarCounterPlatform(platform);
      const trackUnreadViaMonolith = sidebarPlatform
        && shouldUseMonolithCounterSource(sidebarPlatform, inboxMsPlatformsRef.current);
      const unreadMessagesCount = Number(
        eventAttributes.unread_messages_count
        || eventAttributes.unread_messages
        || 0
      );

      if (
        trackUnreadViaMonolith
        && eventAttributes.has_unread_messages === true
        && unreadMessagesCount > 0
        && canViewForUnreadCounter
      ) {
        dispatchRef.current(addUnreadChat({
          platform,
          chat: {
            id: normalizedEventId
          },
          assignedAgentId,
          trustRoomAuthorization: isUnreadOnlyUpdate
        }));
      }

      if (trackUnreadViaMonolith && eventAttributes.has_unread_messages === false) {
        dispatchRef.current(removeUnreadChat({
          platform,
          id: normalizedEventId
        }));
      }

      if (
        hasAssignedAgentUpdate
        && !isUnreadOnlyUpdate
        && !isAssignmentToCurrentUser
        && !canViewForUnreadCounter
        && event?.type
      ) {
        dispatchRef.current(removeUnreadChat({
          platform: event?.type,
          id: normalizedEventId
        }));
      }
    } catch (error) {
      console.error('Error procesando customer_update:', error);
    }
  }, [
    conversationIndexMap,
    conversationsData,
    currentUserContext,
    currentUserId,
    hasNonEmptyObject,
    sanitizeStatusChat,
    normalizeIdToString
  ]);

  // Handler memoizado para eventos 'message_update'.
  const handleMessageUpdate = useCallback((event) => {
    try {
      if (isInboxSocketDebugEnabled()) {
        console.log('Actualización de mensaje recibida:', event);
      }
      dispatchRef.current(
        updateOrAddMessageOptimized({
          ...event,
          platform: event?.channel
        })
      );
    } catch (error) {
      console.error('Error processing message_update:', error);
    }
  }, []);

  const handleChatCounterUpdate = useCallback(
    (event) => {
      const conversationChanges = buildConversationChangesFromCounterEvent(event);
      if (conversationChanges && event?.chat_id) {
        dispatchRef.current(updateOrInsertConversationOptimized({
          id: event.chat_id,
          changes: conversationChanges
        }));
      }

      const applied = dispatchRef.current(updatePlatformCountFromMsEvent(event));
      if (!applied) {
        scheduleMsCounterRefetch();
      }
    },
    [scheduleMsCounterRefetch]
  );

  /**
   * Memoized object with all event handlers for active events.
   */
  const socketEventHandlers = useMemo(
    () => ({
      onNewMessage: handleNewMessage,
      onCustomerUpdate: handleCustomerUpdate,
      onMessageUpdate: handleMessageUpdate,
      onChatCounterUpdate: handleChatCounterUpdate
    }),
    [handleNewMessage, handleCustomerUpdate, handleMessageUpdate, handleChatCounterUpdate]
  );

  /**
   * Memoized return value of the hook.
   */
  const returnValue = useMemo(
    () => ({
      socketEventHandlers
    }),
    [socketEventHandlers]
  );

  return returnValue;
}

export default useInboxSocketActions;
